import {
  thunk,
  Thunk,
  Action,
  action,
  persist,
  computed,
  Computed,
} from 'easy-peasy';
import { getActiveSellerGroupId } from '@seller-hermes/features/portal/portal-local-context';
import { isNoActiveSellersInContext } from '@seller-hermes/features/portal/portal-errors';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import {
  SellerGroupPermissions,
  SellerGroupService,
  SellerGroupUser,
  SellerGroupWhiteLabel,
} from '@seller-hermes/features/seller-group';
import { Injections, IStoreModel } from '../root.store';
import { FetchKeys } from '@seller-hermes/features/ui/constants';
import { productTracker } from '../../product-tracking';

export interface SellerGroupUserStoreModel {
  start: Thunk<SellerGroupUserStoreModel, undefined, Injections, IStoreModel>;
  theme: null | SellerGroupWhiteLabel;
  user: null | SellerGroupUser;
  /**
   * O /me respondeu 403 USER_HAS_NO_ACTIVE_SELLERS_IN_SELLER_GROUP: o usuário
   * é do grupo ativo mas não tem nenhuma filial ativa nele (ADMIN/USER; pra
   * MASTER/INTERNAL o /me devolve `sellers: []`). O guard de rota leva pro
   * cadastro de filial ou pro aviso de sem acesso.
   */
  noActiveSellers: boolean;
  setNoActiveSellers: Action<SellerGroupUserStoreModel, boolean>;
  type: null | SellerGroupUser['type'];
  setSuccessful: Action<SellerGroupUserStoreModel, SellerGroupUser>;
  isLoading: boolean;
  setIsLoading: Action<SellerGroupUserStoreModel, boolean>;
  reset: Action<SellerGroupUserStoreModel>;
  selectedSellersIds: string[];
  useAllSellers: boolean;
  setUseAllSellers: Action<SellerGroupUserStoreModel, boolean>;
  selectedSellers: Computed<
    SellerGroupUserStoreModel,
    SellerGroupUser['sellers']
  >;
  setSelectedSellers: Action<
    SellerGroupUserStoreModel,
    SellerGroupUser['sellers']
  >;
  permissions: SellerGroupPermissions;
  role_name: string;
}

const getThemeFromSellers = (sellers: SellerGroupUser['sellers']) =>
  sellers
    .sort((sellerA, sellerB) => {
      return new Date(sellerA.created_at).getTime() <
        new Date(sellerB.created_at).getTime()
        ? -1
        : 1;
    })
    .filter((seller) => Boolean(seller.white_label))[0]?.white_label;

export const sellerGroupUserStoreModel: SellerGroupUserStoreModel = {
  role_name: '',
  selectedSellersIds: [],
  useAllSellers: false,
  permissions: {
    create_delivery: false,
    create_driver: false,
    create_order: false,
    create_route: false,
    read_delivery: false,
    read_driver: false,
    read_nps: false,
    read_order: false,
    read_route: false,
    update_order_customer: false,
    update_order_destination_address: false,
    update_order_to_manual_handle: false,
    update_route_driver: false,
    update_route_to_canceled: false,
    access_driver_route: false,
    update_order_takeout: false,
    read_dispatch_rules: false,
    update_dispatch_rules: false,
    read_resend_rules: false,
    update_resend_rules: false,
    read_sellers: false,
    update_sellers: false,
    read_order_integrations: false,
    update_order_integrations: false,
    read_carrier_integrations: false,
    update_carrier_integrations: false,
    read_report: false,
    create_report: false,
    update_order_status: false,
    read_inactivity_rules: false,
    update_inactivity_rules: false,
  },
  type: null,
  user: null,
  noActiveSellers: false,
  setNoActiveSellers: action((state, payload) => {
    state.noActiveSellers = payload;
  }),
  theme: null,
  selectedSellers: computed(
    (state) =>
      (state.useAllSellers
        ? state.user?.sellers || []
        : state.selectedSellersIds
            .map((id) => state.user?.sellers.find((seller) => seller.id === id))
            .filter((seller) => Boolean(seller))) as SellerGroupUser['sellers'],
  ),
  setSelectedSellers: action((state, payload) => {
    state.selectedSellersIds = payload.map((seller) => seller.id);
    const theme = getThemeFromSellers(state.selectedSellers);
    state.theme = theme || null;
  }),
  setUseAllSellers: action((state, payload) => {
    state.useAllSellers = payload;
    const sellers = state.useAllSellers
      ? state.user?.sellers || []
      : state.selectedSellers;
    state.theme = getThemeFromSellers(sellers) || null;
  }),
  start: thunk(async (actions, _, { getStoreActions, injections }) => {
    actions.setIsLoading(true);
    try {
      const user = await injections.queryClient.fetchQuery({
        queryKey: FetchKeys.ME,
        queryFn: SellerGroupService.getSellerGroupUser,
      });
      actions.setSuccessful(user);
    } catch (error) {
      const { logout } = getStoreActions().login;
      if (isNoActiveSellersInContext(error)) {
        // Antes mandava pro portal, que devolvia pro painel, que batia no
        // /me de novo (403 a cada poucos segundos em 19/10). E o `user`
        // reidratado da carga anterior seguia no store, então as telas
        // abriam e buscavam com `seller_ids: []`. Agora limpa o usuário e
        // marca o estado; o guard de rota decide pra onde ir.
        actions.reset();
        actions.setNoActiveSellers(true);
        return;
      }

      logout();
      // ADMIN/USER sem filial ativa: o /me retorna esse code (MASTER/INTERNAL
      // recebem sellers vazio e caem no wizard de primeira filial).
      const code = (error as { response?: { data?: { code?: string } } })
        ?.response?.data?.code;
      const messageKey =
        code === 'USER_HAS_NO_ACTIVE_SELLERS_IN_SELLER_GROUP'
          ? 'no-seller-access'
          : 'no-user-related';
      toast.error(
        injections.intl.t(messageKey, { ns: 'errors' }) as string,
      );
    } finally {
      actions.setIsLoading(false);
    }
  }),
  reset: action((state) => {
    state.user = null;
    state.type = null;
    state.theme = null;
    state.noActiveSellers = false;
  }),
  isLoading: true,
  setSuccessful: action((state, user) => {
    state.user = user;
    state.noActiveSellers = false;
    if (state.selectedSellersIds.length === 0) {
      state.selectedSellersIds = user.sellers.map((seller) => seller.id);
    }
    const isCurrentSelectedSellersInUserSellers =
      state.selectedSellersIds.every((seller_id) =>
        user.sellers.some((userSeller) => userSeller.id === seller_id),
      );

    if (!isCurrentSelectedSellersInUserSellers) {
      state.selectedSellersIds = user.sellers.map((seller) => seller.id);
    }
    const activeSellers = state.useAllSellers
      ? user.sellers
      : (state.selectedSellersIds
          .map((seller_id) =>
            user.sellers.find((seller) => seller.id === seller_id),
          )
          .filter((seller) => Boolean(seller)) as SellerGroupUser['sellers']);
    state.theme = getThemeFromSellers(activeSellers) || null;
    const { id, seller_group_id, name: role_name, ...permissions } =
      user.role ?? {
        id: '',
        seller_group_id: '',
        name: '',
        ...sellerGroupUserStoreModel.permissions,
      };
    state.permissions = permissions;
    state.role_name = role_name;
    state.type = user.type;
    const sellerGroupName = user.seller_group?.name;
    const userEmail = user.email;
    productTracker.trackAuth({
      userEmail,
      sellerGroupName,
    });
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: sellerGroupName,
    });
    Sentry.setTag('seller_group_id', user.seller_group?.id);
    Sentry.setTag('seller_group_name', sellerGroupName);
  }),
  setIsLoading: action((state, payload) => {
    state.isLoading = payload;
  }),
};

const sellerGroupScopedStorage = {
  getItem: (key: string) => {
    const sgId = getActiveSellerGroupId();
    return localStorage.getItem(sgId ? `${key}__${sgId}` : key);
  },
  setItem: (key: string, value: string) => {
    const sgId = getActiveSellerGroupId();
    localStorage.setItem(sgId ? `${key}__${sgId}` : key, value);
  },
  removeItem: (key: string) => {
    const sgId = getActiveSellerGroupId();
    localStorage.removeItem(sgId ? `${key}__${sgId}` : key);
  },
};

export default persist(sellerGroupUserStoreModel, {
  allow: ['selectedSellersIds', 'useAllSellers'],
  storage: sellerGroupScopedStorage,
});
